import { IcColorStatusSActive } from '@assets/icons/24x24';
import { Box, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { colors } from '@theme/foundations/colors';
import { toPrecision } from '@utils/math.utils';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MetricStatusEnum } from '@/types/sensor/sensors';

import { metricStatusColor } from '../../../monitoring.types';

export type MetricValue = {
  value: number | null;
  state: MetricStatusEnum | null;
  suffix?: string;
  max?: number | null;
};

export type MetricsModuleProps = {
  metricsComponent?: ReactNode;
  metricsValues?: Map<string, MetricValue>;
};

const MetricGauge = ({ max, value }: Readonly<{ max: number; value: number }>) => {
  const percent = Math.min((value / max) * 100, 100);
  return (
    <Box
      height="4px"
      width="100%"
      background={`linear-gradient(to right, ${colors.blue[500].value} ${percent}%, ${colors.neutral[850].value} ${percent}%)`}
    />
  );
};

export default function MetricsModule({ metricsValues, metricsComponent }: Readonly<MetricsModuleProps>) {
  const { formatMessage } = useIntl();
  return (
    <VStack paddingX={9} paddingY={4} backgroundColor="neutral.700" gap={1} flex="1" height="100%" alignItems="start">
      <Text paddingY="11px" fontWeight="medium" color="neutral.200" textTransform="uppercase">
        <FormattedMessage id={`components.monitoring.sensorDetail.global.metrics.title`} />
      </Text>
      {metricsValues === undefined ? (
        metricsComponent
      ) : (
        <For
          each={Array.from(metricsValues.entries())}
          fallback={
            <Text color="neutral.300">
              <FormattedMessage id="components.monitoring.sensorDetail.global.empty" />
            </Text>
          }
        >
          {([name, { value, suffix = '', state, max }]) => (
            <HStack key={name} width="100%" gap={1} alignItems="start">
              <VStack gap={0} width="100%" alignItems="start">
                <HStack paddingY="7px" width="100%" gap={6} alignItems="start">
                  <Text color="neutral.300" flexShrink={0}>
                    <FormattedMessage id={`components.monitoring.sensorDetail.global.metrics.${name}`} />
                  </Text>
                  <Text fontWeight="medium" marginLeft="auto">
                    {value === null ? formatMessage({ id: 'global.NA' }) : `${toPrecision(value, 3)}${suffix}`}
                  </Text>
                </HStack>
                {max != null && value !== null && <MetricGauge value={value} max={max} />}
              </VStack>
              {value !== null && state !== null && (
                <Icon marginY="7px" boxSize="16px" color={metricStatusColor[state]} as={IcColorStatusSActive} />
              )}
            </HStack>
          )}
        </For>
      )}
    </VStack>
  );
}
